/**
 * Orgs slice — action creators + reducer co-located.
 *
 * Reducer: single source of truth for the user's org list + the active
 * org. Survives navigation (not torn down with the dashboard) so the
 * top-bar switcher stays consistent across editor/dashboard routes.
 *
 * Active-org state has two storage layers:
 *   1. This reducer (in-memory, drives the switcher UI)
 *   2. The `activeOrg` cookie (read by apiFetch to add X-Active-Org)
 * They're kept in lock-step by the setActiveOrg action — never write
 * one without writing the other.
 *
 * Actions: async action creators are plain functions returning a
 * dispatch-and-await — tile-web-v2 doesn't use redux-thunk, so the
 * dispatch is invoked inline. Components call these via `useDispatch()`
 * + the wrapper hook in useOrgs.ts; they never construct action objects
 * by hand.
 */
import type { Dispatch } from 'redux';

import { orgsApi, OrgListing } from '@/features/org/api/orgs.api';
import {
  readActiveOrgFromCookie,
  setActiveOrgCookie,
  ApiError,
} from '@/services/http-client/client';

/* ── Action creators ───────────────────────────────────────────────── */

/** Fetch /api/orgs and replace the slice's list. */
export async function fetchOrgs(dispatch: Dispatch): Promise<OrgListing[]> {
  dispatch({ type: 'ORGS_FETCH_START' });
  try {
    // The first call after login has no active org yet — skip the header.
    const res = await orgsApi.listMine();
    dispatch({ type: 'ORGS_FETCH_SUCCESS', orgs: res.orgs });
    return res.orgs;
  } catch (err: any) {
    dispatch({ type: 'ORGS_FETCH_ERROR', error: err?.message || 'Failed to load orgs' });
    throw err;
  }
}

/** Switch the active org. Updates Redux + cookie atomically. */
export function setActiveOrg(dispatch: Dispatch, slug: string | null): void {
  setActiveOrgCookie(slug);
  dispatch({ type: 'ORGS_SET_ACTIVE', slug });
}

/** Create a new org. Returns the listing-shape row so the switcher can
 *  show it immediately without a refetch. */
export async function createOrg(
  dispatch: Dispatch,
  input: { name: string; slug?: string },
): Promise<OrgListing> {
  const { org } = await orgsApi.create(input);
  const listing: OrgListing = {
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.ownerId,
    myRole: 'owner',
  };
  dispatch({ type: 'ORGS_UPSERT', org: listing });
  // Auto-switch to the new org. UX: creating an org implies you want to
  // start working in it.
  setActiveOrg(dispatch, listing.slug);
  return listing;
}

/** Remove an org (soft-delete on the server, hard-remove from local state). */
export async function removeOrg(dispatch: Dispatch, slug: string): Promise<void> {
  await orgsApi.remove(slug);
  dispatch({ type: 'ORGS_REMOVE', slug });
}

export { ApiError };

/* ── Reducer ───────────────────────────────────────────────────────── */

export interface OrgsState {
  /** Every org the current user is a member of. */
  list: OrgListing[];
  /** Slug of the active org (mirrored to the cookie). null = none picked. */
  activeSlug: string | null;
  /** Timestamp of the last successful /api/orgs fetch. Lets components
   *  skip redundant re-fetches without juggling refs. */
  fetchedAt: number | null;
  loading: boolean;
  error: string | null;
}

const initial: OrgsState = {
  list: [],
  activeSlug: null,
  fetchedAt: null,
  loading: false,
  error: null,
};

type Action =
  | { type: 'ORGS_FETCH_START' }
  | { type: 'ORGS_FETCH_SUCCESS'; orgs: OrgListing[] }
  | { type: 'ORGS_FETCH_ERROR'; error: string }
  | { type: 'ORGS_SET_ACTIVE'; slug: string | null }
  | { type: 'ORGS_UPSERT'; org: OrgListing }
  | { type: 'ORGS_REMOVE'; slug: string };

export function orgs(state: OrgsState = initial, action: Action): OrgsState {
  switch (action.type) {
    case 'ORGS_FETCH_START':
      return { ...state, loading: true, error: null };

    case 'ORGS_FETCH_SUCCESS': {
      // A fresh page load starts with no slug in memory but the user's pick in
      // the cookie — adopt it, or every load reset the user to their personal
      // org. Only with neither (or a stale one) fall back to the first owned
      // org (the personal org), then the first list entry.
      let activeSlug = state.activeSlug ?? readActiveOrgFromCookie();
      const known = action.orgs.some((o) => o.slug === activeSlug);
      if (!activeSlug || !known) {
        const owned = action.orgs.find((o) => o.myRole === 'owner');
        const next = owned?.slug ?? action.orgs[0]?.slug ?? null;
        if (next !== activeSlug) {
          activeSlug = next;
          setActiveOrgCookie(activeSlug);
        }
      }
      return {
        ...state,
        list: action.orgs,
        activeSlug,
        fetchedAt: Date.now(),
        loading: false,
        error: null,
      };
    }

    case 'ORGS_FETCH_ERROR':
      return { ...state, loading: false, error: action.error };

    case 'ORGS_SET_ACTIVE':
      if (state.activeSlug === action.slug) return state;
      setActiveOrgCookie(action.slug);
      return { ...state, activeSlug: action.slug };

    case 'ORGS_UPSERT': {
      const idx = state.list.findIndex((o) => o.id === action.org.id);
      const list =
        idx >= 0
          ? [...state.list.slice(0, idx), action.org, ...state.list.slice(idx + 1)]
          : [...state.list, action.org];
      return { ...state, list };
    }

    case 'ORGS_REMOVE': {
      const list = state.list.filter((o) => o.slug !== action.slug);
      const activeSlug =
        state.activeSlug === action.slug ? list[0]?.slug ?? null : state.activeSlug;
      if (activeSlug !== state.activeSlug) setActiveOrgCookie(activeSlug);
      return { ...state, list, activeSlug };
    }

    default:
      return state;
  }
}
